import React, { useContext, useRef, useState } from "react";
import { IconButton, LinearProgress } from "@mui/material";
import { AttachFile, Delete } from "@mui/icons-material";
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import { AppContext } from "context/appContext";
import { SupportAttachmentInput } from "types/support";

const ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt,.zip,.mp4,.mov,.webm";

interface SupportFileUploadProps {
  onUpload: (file: File) => Promise<SupportAttachmentInput>;
  attachments: SupportAttachmentInput[];
  onRemove: (index: number) => void;
  disabled?: boolean;
  label?: string;
}

const triggerSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  px: 1.5,
  py: 0.75,
  borderRadius: 1,
  border: "1px solid",
  borderColor: "info.main",
  color: "info.main",
  fontSize: "0.8125rem",
  fontWeight: 500,
  lineHeight: 1.5,
  userSelect: "none" as const,
};

function SupportFileUpload({
  onUpload,
  attachments,
  onRemove,
  disabled = false,
  label = "Attachments",
}: SupportFileUploadProps): React.JSX.Element {
  const { showAlert } = useContext(AppContext);
  const busyRef = useRef(false);
  const [uploading, setUploading] = useState(false);
  const isBusy = disabled || uploading;

  const uploadOne = async (file: File) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setUploading(true);
    try {
      await onUpload(file);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to upload attachment";
      showAlert?.(message, "error");
    } finally {
      busyRef.current = false;
      setUploading(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = "";
    if (file) {
      void uploadOne(file);
    }
  };

  return (
    <Box>
      {label ? (
        <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
          {label}
        </Typography>
      ) : null}

      {/* User clicks the real file input (transparent overlay) — no programmatic .click() */}
      <label
        style={{
          position: "relative",
          display: "inline-block",
          cursor: isBusy ? "not-allowed" : "pointer",
          opacity: isBusy ? 0.55 : 1,
        }}
      >
        <Box component="span" sx={{ ...triggerSx, pointerEvents: "none" }}>
          <AttachFile fontSize="small" />
          {uploading ? "Uploading…" : "Add attachment"}
        </Box>
        <input
          data-testid="support-attachment-input"
          type="file"
          accept={ACCEPT}
          disabled={isBusy}
          onChange={handleInputChange}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            margin: 0,
            padding: 0,
            opacity: 0,
            cursor: isBusy ? "not-allowed" : "pointer",
            zIndex: 1,
          }}
        />
      </label>

      {uploading ? (
        <Box mt={1}>
          <LinearProgress sx={{ height: 4, borderRadius: 1 }} />
        </Box>
      ) : null}

      {attachments.length > 0 ? (
        <Box mt={1.5} display="flex" flexDirection="column" gap={0.5}>
          {attachments.filter(Boolean).map((att, index) => (
            <Box
              key={`${att.fileUrl}-${index}`}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ bgcolor: "grey.100", borderRadius: 1, px: 1, py: 0.5 }}
            >
              <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                {att.fileName || att.fileUrl}
              </Typography>
              <IconButton
                type="button"
                size="small"
                onClick={() => onRemove(index)}
                disabled={isBusy}
                aria-label="Remove attachment"
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

export default SupportFileUpload;
